<template>
  <div class="container-fluid pb-5">
    <div v-if="resolveSubmitting || bookingLoading" class="loader-overlay">
      <div class="spinner"></div>
    </div>
    <div class="row justify-content-center">
      <div class="col-12">
        <div class="card shadow-sm border-0 rounded-lg">
          <div class="card-body p-3">
            <h2 class="title-content mb-3 text-left text-primary">Booking Details</h2>
            <div class="row mb-3">
              <div class="col-md-6">
                <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2">
                  <h5 class="mb-0 me-2">Guest Name: </h5>
                  <p class="mb-0 fw-bold">{{ booking.user.name }}</p>
                </div>
                <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2">
                  <h5 class="mb-0 me-2">Phone: </h5>
                  <p class="mb-0 fw-bold">{{ booking.user.mobile_phone }}</p>
                </div>
                <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2">
                  <h5 class="mb-0 me-2">Check-in Date: </h5>
                  <p class="mb-0 fw-bold">{{ booking.start_date }}</p>
                </div>
                <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2">
                  <h5 class="mb-0 me-2">Guests: </h5>
                  <p class="mb-0 fw-bold">{{ booking.number_of_guests }}</p>
                </div>
                <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2">
                  <h5 class="mb-0 me-2">Pets: </h5>
                  <p class="mb-0 fw-bold">{{ booking.pets_count }}</p>
                </div>
              </div>
              <div class="col-md-6">
                <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2">
                  <h5 class="mb-0 me-2">Email: </h5>
                  <p class="mb-0 fw-bold">{{ booking.user.email }}</p>
                </div>
                <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2">
                  <h5 class="mb-0 me-2">Booking Date: </h5>
                  <p class="mb-0 fw-bold">{{ booking.start_date }}</p>
                </div>
                <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2">
                  <h5 class="mb-0 me-2">Check-out Date: </h5>
                  <p class="mb-0 fw-bold">{{ booking.end_date }}</p>
                </div>
                <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2">
                  <h5 class="mb-0 me-2">Children: </h5>
                  <p class="mb-0 fw-bold">{{ booking.childrens_count }}</p>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2">
                  <h5 class="mb-0 me-2">Status: </h5>
                  <p class="mb-0 fw-bold">Confirmed</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Security Deposit Card -->
        <div class="card shadow-sm border-0 rounded-lg mt-4">
          <div class="card-body p-4">
            <h2 class="title-content mb-3 text-left text-primary">{{ $t('payment.security_deposit') }}</h2>
            <div v-if="securityDeposit">
              <div class="row mb-3">
                <div class="col-md-6">
                  <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2">
                    <h5 class="mb-0 me-2">{{ $t('dashboard.deposit_amount') }}:</h5>
                    <p class="mb-0 fw-bold">{{ formatDepositAmount(securityDeposit.amount) }}</p>
                  </div>
                  <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2">
                    <h5 class="mb-0 me-2">{{ $t('dashboard.deposit_status') }}:</h5>
                    <p class="mb-0 fw-bold">
                      <span :class="depositStatusClass(securityDeposit.status)">{{ formatDepositStatus(securityDeposit.status) }}</span>
                    </p>
                  </div>
                  <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2" v-if="securityDeposit.created_at">
                    <h5 class="mb-0 me-2">{{ $t('dashboard.deposit_charged_on') }}:</h5>
                    <p class="mb-0 fw-bold">{{ formatDateTime(securityDeposit.created_at) }}</p>
                  </div>
                  <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2" v-if="securityDeposit.claim_window_ends_at">
                    <h5 class="mb-0 me-2">{{ $t('dashboard.claim_window_ends') }}:</h5>
                    <p class="mb-0 fw-bold">{{ formatDateTime(securityDeposit.claim_window_ends_at) }}</p>
                  </div>
                </div>
                <div class="col-md-6">
                  <!-- <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2">
                    <h5 class="mb-0 me-2">{{ $t('dashboard.guest_confirmed') }}:</h5>
                    <p class="mb-0 fw-bold">{{ securityDeposit.guest_confirmed_at ? formatDateTime(securityDeposit.guest_confirmed_at) : 'No' }}</p>
                  </div>
                  <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2">
                    <h5 class="mb-0 me-2">{{ $t('dashboard.host_confirmed') }}:</h5>
                    <p class="mb-0 fw-bold">{{ securityDeposit.host_no_damage_confirmed_at ? formatDateTime(securityDeposit.host_no_damage_confirmed_at) : 'No' }}</p>
                  </div> -->
                  <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2" v-if="guestCheckInOut">
                    <h5 class="mb-0 me-2">Check-out:</h5>
                    <p class="mb-0 fw-bold">{{ guestCheckInOut.is_checked_out ? (guestCheckInOut.check_out_date_time || 'Yes') : 'Not yet' }}</p>
                  </div>
                  <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2" v-if="securityDeposit.host_claim_reason">
                    <h5 class="mb-0 me-2">{{ $t('payment.damage_claim_reason') }}:</h5>
                    <p class="mb-0 fw-bold">{{ securityDeposit.host_claim_reason }}</p>
                  </div>
                  <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2" v-if="securityDeposit.host_claim_description">
                    <h5 class="mb-0 me-2">Claim Details:</h5>
                    <p class="mb-0 fw-bold">{{ securityDeposit.host_claim_description }}</p>
                  </div>
                </div>
              </div>
              <div class="row" v-if="securityDeposit.guest_refund_amount || securityDeposit.host_release_amount">
                <div class="col-md-6">
                  <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2" v-if="securityDeposit.guest_refund_amount">
                    <h5 class="mb-0 me-2">{{ $t('dashboard.guest_refund') }}:</h5>
                    <p class="mb-0 fw-bold">{{ formatDepositAmount(securityDeposit.guest_refund_amount) }}</p>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2" v-if="securityDeposit.host_release_amount">
                    <h5 class="mb-0 me-2">{{ $t('dashboard.host_release') }}:</h5>
                    <p class="mb-0 fw-bold">{{ formatDepositAmount(securityDeposit.host_release_amount) }}</p>
                  </div>
                </div>
              </div>
              <div v-if="securityDeposit.admin_notes" class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2">
                <h5 class="mb-0 me-2">{{ $t('dashboard.admin_notes') }}:</h5>
                <p class="mb-0 fw-bold">{{ securityDeposit.admin_notes }}</p>
              </div>
              <div v-if="securityDeposit.status === 'host_claimed'" class="mt-3">
                <b-button variant="primary" size="sm" @click="openResolveModal">
                  {{ $t('dashboard.resolve_deposit') }}
                </b-button>
              </div>
            </div>
            <p v-else class="text-muted mb-0">{{ $t('dashboard.no_security_deposit') }}</p>
          </div>
        </div>

        <!-- Invoice History Card -->
        <div class="card shadow-sm border-0 rounded-lg mt-4">
          <div class="card-body p-4">
            <h2 class="title-content mb-3 text-left text-primary">Invoice History</h2>
            <div class="row mb-3">
              <div class="col-md-6">
                <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2">
                  <h5 class="mb-0 me-2">Subtotal Amount: </h5>
                  <p class="mb-0 fw-bold">{{ booking.payment_currency }} {{ payment.subtotalAmount }}</p>
                </div>
                <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2">
                  <h5 class="mb-0 me-2">Discount Amount: </h5>
                  <p class="mb-0 fw-bold">{{ booking.payment_currency }} {{ booking.discount_amount }}</p>
                </div>
                <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2">
                  <h5 class="mb-0 me-2">Total Amount Paid: </h5>
                  <p class="mb-0 fw-bold">{{ booking.payment_currency }} {{ payment.totalAmountPaid }}</p>
                </div>
                <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2">
                  <h5 class="mb-0 me-2">Total Installments: </h5>
                  <p class="mb-0 fw-bold">{{ payment.totalInstallments }}</p>
                </div>
                <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2">
                  <h5 class="mb-0 me-2">Extra Days Payment: </h5>
                  <p class="mb-0 fw-bold">{{ booking.payment_currency }} {{ payment.extraDaysPayment }}</p>
                </div>
              </div>
              <div class="col-md-6">
                <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2">
                  <h5 class="mb-0 me-2">Discounts %: </h5>
                  <p class="mb-0 fw-bold">{{ booking.discount_percentage }}</p>
                </div>
                <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2">
                  <h5 class="mb-0 me-2">Total Payable: </h5>
                  <p class="mb-0 fw-bold">{{ booking.payment_currency }} {{ payment.totalPayable }}</p>
                </div>
                <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2">
                  <h5 class="mb-0 me-2">Remaining Amount: </h5>
                  <p class="mb-0 fw-bold">{{ booking.payment_currency }} {{ payment.remainingAmount }}</p>
                </div>
                <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2">
                  <h5 class="mb-0 me-2">Remaining Installments: </h5>
                  <p class="mb-0 fw-bold">{{ payment.remainingInstallments }}</p>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="listing-content d-flex flex-wrap align-items-center gap-3 mb-2">
                  <h5 class="mb-0 me-2">Commission: </h5>
                  <p class="mb-0 fw-bold">{{ booking.payment_currency }} {{ payment.commission }}</p>
                </div>
              </div>
            </div>

            <h3 class="mt-4 title-content mb-3 text-left text-primary">Payments</h3>
            <div class="booking-table">
              <vue-good-table :columns="columns" :rows="invoices" :search-options="{ enabled: false }"
                :pagination-options="{ enabled: true, perPage: 5 }" styleClass="table table-bordered table-hover">
                <template #table-row="props">
                  <span v-if="props.column.field === 'status'">
                    <span :class="[
                      'px-2 py-1 rounded text-white',
                      props.row.status === 'Paid' ? 'bg-success' :
                        props.row.status === 'Active' ? 'bg-primary' :
                          props.row.status === 'Pending' ? 'bg-warning' :
                            props.row.statusClass === 'deposit-held' ? 'bg-info' :
                              props.row.statusClass === 'deposit-disputed' ? 'bg-danger' :
                                props.row.statusClass === 'deposit-refunded' ? 'bg-secondary' :
                                  props.row.status === 'Failed' ? 'bg-danger' : 'bg-secondary'
                    ]">
                      {{ props.row.status }}
                    </span>
                  </span>
                  <span v-else-if="props.column.field === 'amount'">
                    {{ booking.payment_currency }} {{ props.row.amount }}
                  </span>
                  <span v-else>
                    {{ props.formattedRow[props.column.field] }}
                  </span>
                </template>
              </vue-good-table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal id="resolve-deposit-modal" ref="resolveDepositModal" hide-footer
      :title="$t('dashboard.resolve_deposit')"
      :no-close-on-backdrop="resolveSubmitting"
      :no-close-on-esc="resolveSubmitting"
      :hide-header-close="resolveSubmitting">
      <b-form @submit.prevent="submitResolveDeposit">
        <div v-if="resolveSubmitting" class="resolve-submit-status text-center mb-3">
          <div class="spinner spinner-inline mx-auto"></div>
          <p class="text-muted small mb-0 mt-2">{{ $t('payment.processing') }}</p>
        </div>
        <fieldset :disabled="resolveSubmitting">
          <p><strong>{{ $t('dashboard.deposit_amount') }}:</strong> {{ resolveForm.depositAmountLabel }}</p>
          <div v-if="resolveForm.claimReason || resolveForm.claimDescription" class="claim-details-box mb-3">
            <p v-if="resolveForm.claimReason" class="mb-2">
              <strong>{{ $t('payment.damage_claim_reason') }}:</strong> {{ resolveForm.claimReason }}
            </p>
            <p v-if="resolveForm.claimDescription" class="mb-0">
              <strong>{{ $t('payment.damage_claim_description') }}:</strong>
            </p>
            <p v-if="resolveForm.claimDescription" class="claim-description-text mb-0">{{ resolveForm.claimDescription }}</p>
          </div>
          <b-form-group :label="$t('dashboard.guest_refund_amount')">
            <b-form-input
              v-model.number="resolveForm.guest_refund_amount"
              type="number"
              min="0"
              :max="resolveForm.depositMaxAmount"
              step="0.01"
              required
              @input="onResolveGuestAmountInput"
            />
          </b-form-group>
          <b-form-group :label="$t('dashboard.host_release_amount')">
            <b-form-input
              v-model.number="resolveForm.host_release_amount"
              type="number"
              min="0"
              :max="resolveForm.depositMaxAmount"
              step="0.01"
              required
              @input="onResolveHostAmountInput"
            />
          </b-form-group>
          <p v-if="resolveAmountError" class="text-danger small mb-3">{{ resolveAmountError }}</p>
          <b-form-group :label="$t('dashboard.admin_notes')">
            <b-form-textarea v-model="resolveForm.admin_notes" rows="3" />
          </b-form-group>
        </fieldset>
        <div class="d-flex justify-content-end">
          <b-button class="mr-2" variant="secondary" :disabled="resolveSubmitting"
            @click="$refs.resolveDepositModal.hide()">{{ $t('sweetalert.cancel') }}</b-button>
          <b-button variant="primary" type="submit" :disabled="resolveSubmitting">
            <span v-if="resolveSubmitting" class="spinner spinner-btn mr-2"></span>
            {{ resolveSubmitting ? $t('payment.processing') : $t('common.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import API from '@/api';
import moment from 'moment';

export default {
  components: {
    VueGoodTable
  },
  data() {
    return {
      booking: { user: {} },
      payment: {},
      securityDeposit: null,
      guestCheckInOut: null,
      invoices: [],
      currency: '',
      resolveSubmitting: false,
      bookingLoading: false,
      resolveAmountError: '',
      resolveForm: {
        paymentId: null,
        depositAmountLabel: '',
        depositMaxAmount: 0,
        claimReason: '',
        claimDescription: '',
        guest_refund_amount: 0,
        host_release_amount: 0,
        admin_notes: '',
      },
      columns: [
        { label: 'Transaction ID', field: 'transactionId', sortable: false },
        { label: 'Date', field: 'date', sortable: false },
        { label: 'Amount', field: 'amount', sortable: false },
        { label: 'Type', field: 'type', sortable: false },
        { label: 'Status', field: 'status', sortable: false }
      ]
    };
  },
  created() {
    const bookingId = this.$route.params.id;
    if (bookingId) {
      this.fetchBooking(bookingId);
    }
  },
  watch: {
    '$route.params.id'(newId) {
      if (newId) {
        this.fetchBooking(newId);
      }
    }
  },
  methods: {
    fetchBooking(id) {
      this.bookingLoading = true;
      API.getBookingById(id,
        (res) => {
          this.booking = res.data;
          this.payment = res.meta;
          this.currency = res.data.payment_currency || '--';
          this.securityDeposit = res.meta.securityDeposit || null;
          this.guestCheckInOut = res.meta.guestCheckInOut || null;
          this.invoices = this.buildInvoiceRows(res.meta.invoices || [], this.securityDeposit);
          this.bookingLoading = false;
        },
        (err) => {
          this.booking = { user: {} };
          this.payment = {};
          this.invoices = [];
          this.securityDeposit = null;
          this.bookingLoading = false;
          console.error('Failed to fetch booking:', err);
        }
      );
    },
    buildInvoiceRows(rentInvoices, deposit) {
      const rows = [...rentInvoices];
      if (deposit && deposit.amount) {
        rows.unshift({
          id: `deposit-${deposit.id}`,
          transactionId: 'Security Deposit',
          date: deposit.created_at ? moment(deposit.created_at).format('Y-m-d') : '-',
          amount: parseFloat(deposit.amount).toFixed(2),
          type: 'Security Deposit',
          status: this.formatDepositStatus(deposit.status),
          statusClass: this.depositStatusBadgeClass(deposit.status),
        });
      }
      return rows;
    },
    formatDepositAmount(amount) {
      const currency = (this.booking.payment_currency || this.securityDeposit?.currency || 'AED').toUpperCase();
      return `${currency} ${parseFloat(amount || 0).toFixed(2)}`;
    },
    formatDepositStatus(status) {
      if (!status) return '-';
      return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    },
    depositStatusClass(status) {
      return `badge px-2 py-1 text-white ${this.depositStatusBadgeClass(status)}`;
    },
    depositStatusBadgeClass(status) {
      const map = {
        held: 'bg-info',
        awaiting_release: 'bg-primary',
        host_claimed: 'bg-danger',
        refund_pending: 'bg-warning',
        refunded: 'bg-secondary',
        resolved: 'bg-success',
      };
      return map[status] || 'bg-secondary';
    },
    formatDateTime(value) {
      return value ? moment(value).format('DD-MM-YYYY HH:mm') : '-';
    },
    openResolveModal() {
      if (!this.securityDeposit) return;
      const depositMax = Math.round((parseFloat(this.securityDeposit.amount) || 0) * 100) / 100;
      this.resolveAmountError = '';
      this.resolveForm = {
        paymentId: this.booking.id,
        depositAmountLabel: this.formatDepositAmount(this.securityDeposit.amount),
        depositMaxAmount: depositMax,
        claimReason: this.securityDeposit.host_claim_reason || '',
        claimDescription: this.securityDeposit.host_claim_description || '',
        guest_refund_amount: depositMax,
        host_release_amount: 0,
        admin_notes: '',
      };
      this.$refs.resolveDepositModal.show();
    },
    clampResolveAmount(value) {
      const max = Number(this.resolveForm.depositMaxAmount) || 0;
      let amount = Number(value);
      if (Number.isNaN(amount) || amount < 0) amount = 0;
      if (amount > max) amount = max;
      return Math.round(amount * 100) / 100;
    },
    onResolveGuestAmountInput() {
      this.resolveForm.guest_refund_amount = this.clampResolveAmount(this.resolveForm.guest_refund_amount);
      this.validateResolveAmounts(true);
    },
    onResolveHostAmountInput() {
      this.resolveForm.host_release_amount = this.clampResolveAmount(this.resolveForm.host_release_amount);
      this.validateResolveAmounts(true);
    },
    validateResolveAmounts(showError = true) {
      const max = Number(this.resolveForm.depositMaxAmount) || 0;
      const guest = this.clampResolveAmount(this.resolveForm.guest_refund_amount);
      const host = this.clampResolveAmount(this.resolveForm.host_release_amount);
      this.resolveForm.guest_refund_amount = guest;
      this.resolveForm.host_release_amount = host;

      if (guest > max || host > max) {
        if (showError) {
          this.resolveAmountError = this.$t('dashboard.deposit_amount_exceeds_limit', {
            amount: this.resolveForm.depositAmountLabel,
          });
        }
        return false;
      }

      if (Math.abs((guest + host) - max) > 0.01) {
        if (showError) {
          this.resolveAmountError = this.$t('dashboard.deposit_split_must_equal_total', {
            amount: this.resolveForm.depositAmountLabel,
          });
        }
        return false;
      }

      this.resolveAmountError = '';
      return true;
    },
    submitResolveDeposit() {
      if (!this.resolveForm.paymentId || this.resolveSubmitting) return;
      if (!this.validateResolveAmounts(true)) return;
      this.resolveSubmitting = true;
      API.adminResolveSecurityDeposit(this.resolveForm.paymentId, {
        guest_refund_amount: this.resolveForm.guest_refund_amount,
        host_release_amount: this.resolveForm.host_release_amount,
        admin_notes: this.resolveForm.admin_notes,
      }, (data) => {
        if (data.status == 200) {
          this.resolveAmountError = '';
          if (data.data) {
            this.securityDeposit = data.data;
          } else if (this.securityDeposit) {
            this.securityDeposit = {
              ...this.securityDeposit,
              status: 'resolved',
              guest_refund_amount: this.resolveForm.guest_refund_amount,
              host_release_amount: this.resolveForm.host_release_amount,
              admin_notes: this.resolveForm.admin_notes,
            };
          }
          this.invoices = this.buildInvoiceRows(
            (this.payment && this.payment.invoices) || [],
            this.securityDeposit
          );
          this.$refs.resolveDepositModal.hide();
          this.resolveSubmitting = false;
          this.fetchBooking(this.booking.id);
        } else {
          this.resolveSubmitting = false;
          this.resolveAmountError = data.message || this.$t('errors.failed_load_data');
        }
      }, (error) => {
        this.resolveSubmitting = false;
        this.resolveAmountError = error?.data?.message
          || error?.message
          || this.$t('errors.failed_load_data');
      });
    },
    formatCurrency(amount) {
      if (!amount) return `${this.currency} 0.00`;
      return `${this.currency} ${parseFloat(amount).toFixed(2)}`;
    }
  }
};
</script>

<style scoped>
.card {
  border-radius: 1.5rem;
}

.resolve-submit-status {
  background: #f7fafc;
  border-radius: 8px;
  padding: 12px;
}

.spinner-inline {
  width: 28px;
  height: 28px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #70D3FF;
  border-radius: 50%;
  animation: resolve-spin 1s linear infinite;
}

.spinner-btn {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #fff;
  border-radius: 50%;
  vertical-align: -2px;
  animation: resolve-spin 1s linear infinite;
}

@keyframes resolve-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

th,
td {
  border-left: 1px solid #dee2e6 !important;
  border-right: 1px solid #dee2e6 !important;
}

thead th:first-child {
  border-left: 1px solid #dee2e6 !important;
}

thead th:last-child {
  border-right: 1px solid #dee2e6 !important;
}

.listing-content h5 {
  width: 30%;
}

.card .title-content {
  font-size: 22px;
}

.client-dashbboard .main-panel .card {
  padding: 0;
}

@media (max-width: 1199px) {
  .listing-content h5 {
    width: 40%;
  }
}

@media (max-width: 767px) {
  .client-dashbboard .main-panel .content-wrapper {
    padding: 25px 0 !important;
  }
}

@media (max-width: 574px) {
  .listing-content h5 {
    width: 50%;
    font-size: 14px;
  }

  .container-fluid {
    padding: 15px 0 0;
  }
}

.claim-details-box {
  background: #f8fbff;
  border: 1px solid #d9ecff;
  border-radius: 8px;
  padding: 12px 14px;
}

.claim-description-text {
  margin-top: 6px;
  white-space: pre-wrap;
  word-break: break-word;
  color: #333;
  line-height: 1.5;
}
</style>
